import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Sparkles, LayoutGrid, FolderOpen, Menu, X, Radio, User, LogOut, Cloud, Crown, ListMusic, Globe } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/AuthModal";

const navItems = [
  { path: "/", label: "Home", icon: Home },
  { path: "/studio", label: "Studio", icon: Radio },
  { path: "/playlists", label: "Playlists", icon: ListMusic },
  { path: "/create", label: "Subliminals", icon: Sparkles },
  { path: "/discover", label: "Discover", icon: Globe },
  { path: "/pricing", label: "Pricing", icon: Crown },
];

const Navigation = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const { user, isAuthenticated, isPremium, logout } = useAuth();

  return (
    <>
      {/* Desktop Navigation - Floating Dock */}
      <nav className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 hidden md:block">
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, type: "spring", stiffness: 100 }}
          className="glass-card px-2 py-2 flex items-center gap-1"
        >
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className="relative"
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`
                    flex items-center gap-2 px-4 py-2.5 rounded-xl transition-colors duration-200
                    ${isActive 
                      ? 'bg-violet-600 text-white' 
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }
                  `}
                >
                  <Icon size={18} />
                  <span className="text-sm font-medium">{item.label}</span>
                </motion.div>
              </Link>
            );
          })}

          {/* Auth Button */}
          <div className="border-l border-white/10 ml-1 pl-2">
            {isAuthenticated ? (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-2 text-cyan-400">
                  <Cloud size={16} />
                  <span className="text-xs">{user?.name?.split(' ')[0]}</span>
                  {isPremium && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-400 font-medium">
                      PRO
                    </span>
                  )}
                  {user?.is_admin && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-medium">
                      ADMIN
                    </span>
                  )}
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={logout}
                  data-testid="nav-logout"
                  className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                >
                  <LogOut size={18} />
                </motion.button>
              </div>
            ) : (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setAuthModalOpen(true)}
                data-testid="nav-login"
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
              >
                <User size={18} />
                <span className="text-sm font-medium">Sign In</span>
              </motion.button>
            )}
          </div>
        </motion.div>
      </nav>

      {/* Mobile Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 md:hidden">
        <div className="glass-heavy px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="mobile-logo-link">
            <span
              className="font-bold text-lg text-white tracking-wide"
              style={{
                background: "linear-gradient(135deg, #e879f9 0%, #a78bfa 50%, #67e8f9 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                filter: "drop-shadow(0 0 8px rgba(168, 85, 247, 0.4))",
              }}
            >
              Subspace
            </span>
          </Link>

          <div className="flex items-center gap-2">
            {isAuthenticated ? (
              <div className="flex items-center gap-1 text-xs text-cyan-400">
                <Cloud size={14} />
                <span>{user?.name?.split(' ')[0]}</span>
                {isPremium && (
                  <span className="px-1 py-0.5 rounded bg-violet-500/20 text-violet-400 text-[10px]">
                    PRO
                  </span>
                )}
                {user?.is_admin && (
                  <span className="px-1 py-0.5 rounded bg-amber-500/20 text-amber-400 text-[10px]">
                    ADMIN
                  </span>
                )}
              </div>
            ) : (
              <button
                onClick={() => setAuthModalOpen(true)}
                className="p-2 text-gray-400"
              >
                <User size={20} />
              </button>
            )}
            
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-btn"
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="glass-heavy border-t border-white/5"
            >
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`
                      flex items-center gap-3 px-4 py-3 border-b border-white/5
                      ${isActive ? 'text-violet-400 bg-violet-500/10' : 'text-gray-400'}
                    `}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}

              {isAuthenticated ? (
                <button
                  onClick={() => {
                    logout();
                    setMobileOpen(false);
                  }}
                  className="flex items-center gap-3 px-4 py-3 text-red-400 w-full"
                >
                  <LogOut size={20} />
                  <span>Sign Out</span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    setAuthModalOpen(true);
                    setMobileOpen(false);
                  }}
                  className="flex items-center gap-3 px-4 py-3 text-cyan-400 w-full"
                >
                  <User size={20} />
                  <span>Sign In / Register</span>
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Auth Modal */}
      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} />
    </>
  );
};

export default Navigation;
